/**
 * The countries an album is about.
 *
 * Beside the location field, not instead of it. "Location" is free text and
 * should stay that way — "the cabin", "Sarah's wedding", "Kraków" are all real
 * answers and only one of them is a country. But a free-text field cannot be
 * counted, which is why the home screen used to work its country total out from
 * flag emoji: it depended on somebody having picked a flag rather than a
 * picture of a beach, and on having searched the picker in English.
 *
 * So the countries are asked for once, properly, and everything downstream
 * reads two letters at a time.
 *
 * **Several, in order.** A fortnight of driving is one album and three
 * countries, and the old single field forced a choice between filing it under
 * Slovenia and filing it under Italy. The first one chosen is the one shown
 * wherever there is room for a single flag, so the order is kept rather than
 * sorted — "Slovenia, Croatia, Italy" says something that alphabetical order
 * does not.
 *
 * **It suggests rather than asks again.** Albums are already called "Słowenia
 * 2026" and located in "Barcelona" — the name usually contains the answer. The
 * suggestion appears under the field and is offered, never applied: guessing
 * silently is how an album about a band called Chicago ends up filed under the
 * United States.
 *
 * Names come from `Intl.DisplayNames`, so the list is in the reader's own
 * language with nothing bundled and nothing translated by us. Typing matches
 * that language *and* English, because somebody reading in Polish may well type
 * "Germany" — it is what the place is called on their boarding pass.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocale, useT } from "../../lib/i18n.js";
import {
  countryName,
  detectCountry,
  flagFor,
  searchCountries,
} from "../../lib/countries.js";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import "./CountryField.css";

interface CountryFieldProps {
  value: string[];
  onChange: (codes: string[]) => void;
  /** The album name and location, read for a suggestion. */
  hints: string[];
}

export function CountryField({ value, onChange, hints }: CountryFieldProps) {
  const t = useT();
  const { locale } = useLocale();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  /* Already-chosen countries drop out of the list rather than being offered
     again and silently doing nothing when clicked. */
  const results = useMemo(
    () =>
      searchCountries(query, locale).filter(
        (country) => !value.includes(country.code),
      ),
    [query, locale, value],
  );

  /*
   * What the album already says. Only while nothing is chosen: re-reading the
   * name after somebody has answered would offer to add our guess to their
   * answer, which is the wrong way round.
   */
  const suggestion = useMemo(() => {
    if (value.length > 0 || dismissed) return null;
    for (const hint of hints) {
      const found = detectCountry(hint, locale);
      if (found) return found;
    }
    return null;
  }, [value, dismissed, hints, locale]);

  /* Clicking anywhere else is how a dropdown is dismissed; Escape is the other. */
  useEffect(() => {
    if (!open) return;

    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const add = (code: string) => {
    if (value.includes(code)) return;
    onChange([...value, code]);
    setQuery("");
    /* Left open: adding one country is usually the first of two or three. */
  };

  const remove = (code: string) => {
    onChange(value.filter((c) => c !== code));
    /* Chosen then removed is an answer: do not re-suggest. */
    setDismissed(true);
  };

  return (
    <Field
      label={t("Countries")}
      hint={t("Optional. Counted in your statistics and shown on your map.")}
    >
      <div className="fc-country" ref={box}>
        {value.length > 0 && (
          <ul className="fc-country__chips">
            {value.map((code, index) => (
              <li key={code} className="fc-country__chosen">
                <EmojiIcon
                  emoji={flagFor(code)}
                  label={countryName(code, locale)}
                />
                <span>{countryName(code, locale)}</span>
                {/* The first is the one shown where only one fits. */}
                {index === 0 && value.length > 1 && (
                  <span className="fc-country__first">{t("main")}</span>
                )}
                <button
                  type="button"
                  className="fc-country__clear"
                  aria-label={`${t("Remove")} ${countryName(code, locale)}`}
                  onClick={() => remove(code)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}

        <Input
          value={query}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls={listId}
          placeholder={
            value.length > 0
              ? t("Add another country")
              : t("Search for a country")
          }
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setOpen(false);
            } else if (event.key === "Enter" && results[0]) {
              /* Enter takes the top match — the field is inside a form and
                 would otherwise submit the dialog on the way past. */
              event.preventDefault();
              add(results[0].code);
            } else if (
              event.key === "Backspace" &&
              query === "" &&
              value.length > 0
            ) {
              /* Backspace on an empty box removes the last chip, which is what
                 every other chip field does. */
              event.preventDefault();
              remove(value[value.length - 1]!);
            }
          }}
        />

        {open && results.length > 0 && (
          <ul className="fc-country__list" id={listId} role="listbox">
            {results.map((country) => (
              <li key={country.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => add(country.code)}
                >
                  <EmojiIcon emoji={flagFor(country.code)} />
                  <span>{country.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {suggestion && (
          <div className="fc-country__suggestion">
            <button type="button" onClick={() => add(suggestion.code)}>
              <EmojiIcon emoji={flagFor(suggestion.code)} />
              {t("Use")} {suggestion.name}?
            </button>
            <button
              type="button"
              className="fc-country__dismiss"
              aria-label={t("No country")}
              onClick={() => setDismissed(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </Field>
  );
}
